import React, { useState, useCallback, useContext } from 'react'
import { Button, Dialog, DialogContent, DialogTitle, DialogActions, MenuItem, TextField } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { gray } from '../../../utils/colors'
import { FirebaseContext } from '../../Firebase/context'
import { useDispatch } from 'react-redux'
import { showError, showSuccess } from '../../../actions/misc/misc.actions'
import { api } from '../../../services/api'
import { APP_OPTIONS } from '../../../utils'
import { sendPasswordResetEmail } from 'firebase/auth'

const StaffPasswordResetDialog = ({ open, onClose, staffMember }) => {
	const classes = useStyles()
	const dispatch = useDispatch()
	const firebase = useContext(FirebaseContext)
	const [showManualResetForm, setShowManualResetForm] = useState(false)
	const [selectedApp, setSelectedApp] = useState('')
	const [password, setPassword] = useState('')

	const handleAppSelection = useCallback((e) => setSelectedApp(e?.target?.value), [])
	const handlePwInput = useCallback((e) => setPassword(e?.target?.value), [])

	const handleManualPasswordReset = useCallback(() => {
		setShowManualResetForm(true)
	}, [])

	const handleClose = useCallback(() => {
		if (onClose) {
			onClose()
		}

		setTimeout(() => {
			setSelectedApp('')
			setPassword('')
			setShowManualResetForm(false)
		}, 200)
	}, [onClose])

	const handleSendPasswordResetLink = useCallback(async () => {
		if (!staffMember || !firebase) {
			return
		}
		const { email, uid_map } = staffMember
		const uids = uid_map || []
		for (const uid of uids) {
			const { app } = uid
			const isAdmin = app === 'admin'
			const isTeam = app === 'team'
			try {
				if (isAdmin) {
					await sendPasswordResetEmail(firebase.auth, email)
					dispatch(showSuccess('Admin Password Reset Link was sent.'))
				} else if (isTeam) {
					await sendPasswordResetEmail(firebase.tmAuth, email)
					dispatch(showSuccess('Team Member Password Reset Link was sent.'))
				}
			} catch (e) {
				console.log('heck', e)
				if (isAdmin) {
					dispatch(showError('Admin Password Reset Link failed to send.'))
				} else if (isTeam) {
					dispatch(showError('Team Member Reset Link failed to send.'))
				}
			}
		}

		handleClose()
	}, [firebase, staffMember, handleClose])

	const handlePasswordResetSubmission = useCallback(async () => {
		if (selectedApp === '' || password === '' || !staffMember) {
			return
		}

		try {
			const { uid_map } = staffMember || []
			const uid =
				uid_map.find(({ app }) => {
					return app === selectedApp
				}) || null
			if (uid) {
				const {
					data: { data, errors }
				} = await api.patch('/team/credentials/update', { uid, password })
				if (errors.length > 0) {
					dispatch(showError('Password reset failed.'))
				}
				if (data.length > 0) {
					dispatch(showSuccess('Password reset successful.'))
				}
			}
		} catch (e) {
			console.log(e)
			dispatch(showError('Password reset failed.'))
		} finally {
			handleClose()
		}
	}, [staffMember, password, selectedApp])

	return (
		<Dialog
			open={open}
			maxWidth="lg"
			onClose={handleClose}
			PaperProps={{
				style: {
					minWidth: '448px',
					maxWidth: '448px',
					width: '448px'
				}
			}}>
			<DialogTitle className={classes.dialogTitle}>
				{showManualResetForm ? 'Manual Password Reset' : 'Password Reset'}
			</DialogTitle>
			<DialogContent className={classes.contentRoot}>
				{showManualResetForm ? (
					<div className={classes.formContainer}>
						<TextField
							select
							label="App"
							variant="filled"
							value={selectedApp}
							onChange={handleAppSelection}>
							{staffMember?.apps.map((app) => {
								const value = APP_OPTIONS[app]
								return (
									<MenuItem key={app} value={value}>
										{app}
									</MenuItem>
								)
							})}
						</TextField>
						<TextField
							label="New Password"
							variant="filled"
							value={password}
							onChange={handlePwInput}
						/>
					</div>
				) : (
					<div className={classes.resetSelectionContainer}>
						<Button variant="contained" color="secondary" onClick={handleSendPasswordResetLink}>
							Send Password reset link
						</Button>
						<div className={classes.dividerContainer}>
							<div className={classes.divider}></div>
							<div className={classes.orText}>Or</div>
							<div className={classes.divider}></div>
						</div>
						<Button variant="outlined" color="secondary" onClick={handleManualPasswordReset}>
							Reset Password Manually
						</Button>
					</div>
				)}
			</DialogContent>
			{showManualResetForm ? (
				<DialogActions className={classes.dialogActions}>
					<Button
						variant="contained"
						disabled={selectedApp === '' || password === ''}
						onClick={handlePasswordResetSubmission}
						color="secondary">
						Save
					</Button>
					<Button variant="outlined" onClick={handleClose} color="secondary">
						Cancel
					</Button>
				</DialogActions>
			) : null}
		</Dialog>
	)
}

const useStyles = makeStyles((theme) => ({
	contentRoot: {
		padding: '1.625rem 0'
	},
	dialogTitle: {
		textTransform: 'uppercase',
		fontWeight: 600,
		fontSize: '1rem'
	},
	dialogActions: {
		justifyContent: 'flex-start',
		padding: '0.55rem 0'
	},
	formContainer: {
		padding: '0',
		display: 'flex',
		flexDirection: 'column',
		gap: '1.25rem',
		'& .MuiFilledInput-underline': {
			minWidth: '100% !important'
		},
		'& .MuiTextField-root': {
			minWidth: '100% !important'
		}
	},
	resetSelectionContainer: {
		border: '1.5px solid',
		borderColor: gray[500],
		padding: '1.75rem 2.5rem',
		borderRadius: '0.625rem',
		display: 'flex',
		flexDirection: 'column',
		gap: '1.25rem'
	},
	dividerContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'relative',
		gap: '1rem'
	},
	divider: {
		height: '1px',
		flex: 1,
		backgroundColor: gray[500]
	},
	orText: {
		textTransform: 'uppercase',
		fontFamily: 'Haboro Soft',
		fontSize: '0.875rem',
		fontWeight: 800
	}
}))

export default StaffPasswordResetDialog
